import {
  DeltaType,
  Operation,
  type PathBrush,
  type Render,
} from '@meitu/whee-infinite-canvas';
import useAIToolsCanvasStore, { setHasMask } from '@/store/useAIToolsCanvasStore';

export interface AIEraserManualMaskHistorySnapshot {
  objects: any[];
}

let applyingManualMaskHistory = false;

/** 获取当前笔刷的 mask 容器。 */
function getManualMaskContainer(render: Render | null) {
  const brush = render?._FC.freeDrawingBrush as PathBrush | undefined;
  return brush?.maskContainer;
}

/** 过滤自动识别生成的 SmartMask，仅把用户手动画布 mask 纳入手动历史。 */
function getManualMaskObjects(render: Render | null): any[] {
  const container = getManualMaskContainer(render);
  return container?.getObjects?.().filter((obj: any) => obj?.maskType !== 'smart') ?? [];
}

/** 复制一份 Fabric 对象，作为不可变历史快照保存。 */
function cloneMaskObject(object: any): Promise<any | null> {
  if (!object || typeof object.clone !== 'function') return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (cloned: any) => {
      if (settled) return;
      settled = true;
      resolve(cloned ?? null);
    };
    const maybePromise = object.clone((cloned: any) => finish(cloned));
    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.then(finish).catch(() => finish(null));
    } else if (maybePromise && object.clone.length === 0) {
      finish(maybePromise);
    }
  });
}

/** 生成当前手动画布 mask 快照。 */
export async function createAIEraserManualMaskHistorySnapshot(
  render: Render | null,
): Promise<AIEraserManualMaskHistorySnapshot> {
  const clones = await Promise.all(getManualMaskObjects(render).map(cloneMaskObject));
  return { objects: clones.filter(Boolean) };
}

/** 判断两个手动 mask 快照是否一致。 */
function isSameManualMaskHistorySnapshot(
  before: AIEraserManualMaskHistorySnapshot,
  after: AIEraserManualMaskHistorySnapshot,
): boolean {
  return JSON.stringify(before.objects.map((obj) => obj?.toObject?.() ?? obj))
    === JSON.stringify(after.objects.map((obj) => obj?.toObject?.() ?? obj));
}

/** 根据手动对象数量同步「开始消除」按钮可用状态。 */
function syncAIEraserHasMask(render: Render | null): void {
  setHasMask(getManualMaskObjects(render).length > 0);
}

/** 将手动画布 mask 恢复到指定快照，供撤销/重做 handler 调用。 */
function applyAIEraserManualMaskHistorySnapshot(
  render: Render,
  snapshot: AIEraserManualMaskHistorySnapshot,
): void {
  const container = getManualMaskContainer(render);
  if (!container) return;

  applyingManualMaskHistory = true;
  try {
    const currentManualObjects = getManualMaskObjects(render);
    if (currentManualObjects.length) {
      container.remove(...currentManualObjects);
    }
    if (snapshot.objects.length) {
      container.add(...snapshot.objects);
    }
    render._FC.renderAll();
    syncAIEraserHasMask(render);
  } finally {
    window.requestAnimationFrame(() => {
      applyingManualMaskHistory = false;
    });
  }
}

/** 当前是否正在由历史 handler 恢复手动 mask。 */
export function isApplyingAIEraserManualMaskHistory(): boolean {
  return applyingManualMaskHistory;
}

/** 执行不需要写入手动 mask 历史的画布清理操作。 */
export function runWithoutAIEraserManualMaskHistory(callback: () => void): void {
  applyingManualMaskHistory = true;
  try {
    callback();
  } finally {
    window.requestAnimationFrame(() => {
      applyingManualMaskHistory = false;
    });
  }
}

/** 提交手动画布 mask 变化到画布历史栈。 */
export function submitAIEraserManualMaskHistory(
  before: AIEraserManualMaskHistorySnapshot,
  after: AIEraserManualMaskHistorySnapshot,
  onApply?: (snapshot: AIEraserManualMaskHistorySnapshot) => void,
): void {
  const { render, historyPlugins } = useAIToolsCanvasStore.getState();
  if (!render || !historyPlugins || isSameManualMaskHistorySnapshot(before, after)) return;

  const operation = new Operation({
    type: DeltaType.Handler,
    preData: null,
    afterData: null,
    selectable: false,
    undoHandler: () => {
      applyAIEraserManualMaskHistorySnapshot(render, before);
      onApply?.(before);
    },
    redoHandler: () => {
      applyAIEraserManualMaskHistorySnapshot(render, after);
      onApply?.(after);
    },
  }, render);
  historyPlugins.submit(operation);
}
